import { zodResolver } from '@hookform/resolvers/zod';
import {
  apiClientControllerCreate,
  apiClientControllerList,
  apiClientControllerRevoke,
} from '@kalba/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Check, Copy, KeyRound, ShieldOff } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { AppLayout } from '../../../components/layout/app-layout';
import { PageHeader } from '../../../components/layout/page-header';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Skeleton } from '../../../components/ui/skeleton';
import { usePermissions } from '../../../hooks/use-permissions';
import { cn } from '../../../lib/utils';
import { useAuthStore } from '../../../stores/auth.store';

// ── Route ─────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/config/api-clients/')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('api-client:view')) throw redirect({ to: '/dashboard' });
  },
  component: ApiClientsPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface ApiClient {
  id: string;
  name: string;
  keyPrefix: string;
  scopes: string[];
  revokedAt: string | null;
  expiresAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
  createdBy: { id: string; name: string } | null;
}

interface CreatedApiClient {
  id: string;
  name: string;
  scopes: string[];
  keyPrefix: string;
  expiresAt: string | null;
  createdAt: string;
  apiKey: string;
}

// ── Form schema ───────────────────────────────────────────────────────────────

const createSchema = z.object({
  name: z.string().min(2, 'Name is required'),
  scopes: z.string().min(1, 'At least one scope is required (comma-separated)'),
  expiresAt: z.string().optional(),
});
type CreateFormData = z.infer<typeof createSchema>;

// ── Helpers ────────────────────────────────────────────────────────────────────

function statusOf(client: ApiClient): { label: string; className: string } {
  if (client.revokedAt) return { label: 'Revoked', className: 'bg-gray-100 text-gray-500' };
  if (client.expiresAt && new Date(client.expiresAt) < new Date()) {
    return { label: 'Expired', className: 'bg-amber-50 text-amber-700' };
  }
  return { label: 'Active', className: 'bg-emerald-50 text-emerald-700' };
}

function fmt(iso: string | null) {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-AE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

// ── Page ──────────────────────────────────────────────────────────────────────

function ApiClientsPage() {
  const { can } = usePermissions();
  const canManage = can('api-client:manage');
  const qc = useQueryClient();

  const [showForm, setShowForm] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiClient | null>(null);
  const [createdKey, setCreatedKey] = useState<CreatedApiClient | null>(null);

  const listKey = ['api-clients'];

  const { data: clients = [], isLoading } = useQuery({
    queryKey: listKey,
    queryFn: () => apiClientControllerList() as Promise<ApiClient[]>,
  });

  const createMutation = useMutation({
    mutationFn: (dto: { name: string; scopes: string[]; expiresAt?: string }) =>
      apiClientControllerCreate({ requestBody: dto as never }) as Promise<CreatedApiClient>,
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: listKey });
      setShowForm(false);
      setCreatedKey(result);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => apiClientControllerRevoke({ id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: listKey });
      setRevokeTarget(null);
    },
  });

  return (
    <AppLayout>
      <PageHeader
        title="API Clients"
        description="Keys issued to external systems (e.g. Tahseel) for server-to-server integration"
        actions={
          canManage && (
            <Button variant="primary" size="sm" onClick={() => setShowForm(true)}>
              <KeyRound size={14} className="mr-1.5" />
              Issue New Key
            </Button>
          )
        }
      />

      <div className="p-6">
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2].map((n) => (
              <Skeleton key={n} className="h-16 w-full rounded-xl" />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-gray-200 bg-white shadow-theme-sm overflow-hidden">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {['Name', 'Key', 'Scopes', 'Status', 'Last Used', 'Expires', 'Actions'].map(
                    (h) => (
                      <th
                        key={h}
                        className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {clients.map((client) => {
                  const status = statusOf(client);
                  return (
                    <tr key={client.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4 font-medium text-gray-900">{client.name}</td>
                      <td className="px-6 py-4 font-mono text-xs text-gray-500">
                        {client.keyPrefix}…
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {client.scopes.map((scope) => (
                            <span
                              key={scope}
                              className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700"
                            >
                              {scope}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={cn(
                            'rounded-full px-2.5 py-0.5 text-xs font-medium',
                            status.className,
                          )}
                        >
                          {status.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-400 text-xs">{fmt(client.lastUsedAt)}</td>
                      <td className="px-6 py-4 text-gray-400 text-xs">
                        {client.expiresAt ? fmt(client.expiresAt) : 'Never'}
                      </td>
                      <td className="px-6 py-4">
                        {canManage && !client.revokedAt && (
                          <button
                            type="button"
                            onClick={() => setRevokeTarget(client)}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors"
                            title="Revoke"
                          >
                            <ShieldOff size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {clients.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-gray-400">
                      No API clients issued yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CreateKeyDialog
        open={showForm}
        isPending={createMutation.isPending}
        error={createMutation.error as Error | null}
        onClose={() => setShowForm(false)}
        onSubmit={(data) =>
          createMutation.mutate({
            name: data.name,
            scopes: data.scopes
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean),
            expiresAt: data.expiresAt || undefined,
          })
        }
      />

      <RevealKeyDialog createdKey={createdKey} onClose={() => setCreatedKey(null)} />

      <Dialog open={revokeTarget !== null} onOpenChange={() => setRevokeTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke API Key</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            This immediately stops <span className="font-semibold">{revokeTarget?.name}</span> from
            authenticating. This cannot be undone — issue a new key if the integration needs to keep
            working.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              className="bg-error-500 hover:bg-error-600 focus:ring-error-200"
              disabled={revokeMutation.isPending}
              onClick={() => revokeTarget && revokeMutation.mutate(revokeTarget.id)}
            >
              {revokeMutation.isPending ? 'Revoking…' : 'Revoke'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

// ── Create dialog ─────────────────────────────────────────────────────────────

function CreateKeyDialog({
  open,
  isPending,
  error,
  onClose,
  onSubmit,
}: {
  open: boolean;
  isPending: boolean;
  error: Error | null;
  onClose: () => void;
  onSubmit: (data: CreateFormData) => void;
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateFormData>({
    resolver: zodResolver(createSchema),
    defaultValues: { name: '', scopes: '', expiresAt: '' },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Issue New API Key</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={handleSubmit((data) => {
            onSubmit(data);
            reset();
          })}
          className="space-y-4"
        >
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700">Name</label>
            <input
              {...register('name')}
              placeholder="e.g. Tahseel Production"
              className={inputCls(!!errors.name)}
            />
            {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name.message}</p>}
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700">
              Scopes (comma-separated)
            </label>
            <input
              {...register('scopes')}
              placeholder="tahseel-inbound:receive"
              className={inputCls(!!errors.scopes)}
            />
            {errors.scopes && <p className="mt-1 text-xs text-red-600">{errors.scopes.message}</p>}
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700">
              Expires (optional — leave blank for a key that never expires)
            </label>
            <input type="date" {...register('expiresAt')} className={inputCls(false)} />
          </div>

          {error && (
            <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">
              {error.message}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isPending}>
              {isPending ? 'Issuing…' : 'Issue Key'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Reveal-once dialog ────────────────────────────────────────────────────────

function RevealKeyDialog({
  createdKey,
  onClose,
}: {
  createdKey: CreatedApiClient | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  if (!createdKey) return null;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{createdKey.name} — Key Created</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-600">
          Copy this key now and share it securely with the integration partner.{' '}
          <span className="font-semibold text-error-600">
            It will not be shown again after you close this dialog.
          </span>
        </p>
        <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
          <code className="flex-1 overflow-x-auto whitespace-nowrap font-mono text-xs text-gray-800">
            {createdKey.apiKey}
          </code>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(createdKey.apiKey);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            className="flex shrink-0 items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 transition-colors"
          >
            {copied ? <Check size={13} className="text-success-600" /> : <Copy size={13} />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <DialogFooter>
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function inputCls(hasError: boolean) {
  return cn(
    'w-full rounded-xl border px-3 py-2 text-sm outline-none transition-colors',
    hasError
      ? 'border-red-300 focus:border-red-400 focus:ring-1 focus:ring-red-400'
      : 'border-gray-200 focus:border-brand-500 focus:ring-1 focus:ring-brand-500',
  );
}
